import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { isString, isObject } from 'lodash';

import {
    Button,
    Card,
    FormGroup,
    Icon,
    InputGroup,
    Switch,
    Toaster,
} from '@blueprintjs/core';
import {
    Elevation,
    IconSize,
    Intent,
    Position
} from '@blueprintjs/core';
import {
    IconNames
} from '@blueprintjs/icons';
import { Tooltip2 } from '@blueprintjs/popover2';
import useGenerateId from '$utils/hooks/useGenerateId';

import logoSvg from '$public/images/login.logo.svg';
import { useConfig } from '$root/contexts/config';
import { useAuth } from '$root/contexts/auth.js';
import { PagePath } from '$pages';
import DarkModeSwitch from '$root/components/DarkModeSwitch';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 25rem;
    min-height: 100%;
    margin: 0 auto 0 auto;
    padding-top: 8rem;
    width: 22rem;
`;

const Logo = styled.img`
    margin: 0 auto 0 auto;
    padding-bottom: 2rem;
    width: 128px;
`;

const FormCard = styled(Card)`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

export default function LoginPage(props) {

    const [ showPassword, setShowPassword ] = useState(false);
    const [ email, setEmail ] = useState(__DEVELOPMENT__ ? 'torokze@gmail.com' : '');
    const [ password, setPassword ] = useState(__DEVELOPMENT__ ? 'xlcjvo' : '');
    const [ isWorking, setIsWorking ] = useState(false);
    const toasterRef = useRef();

    const generateId = useGenerateId();

    const { login, user } = useAuth();
    const navigate = useNavigate();

    let passwordInput;

    const onEmailChange = (evt) => {
        setEmail(old => evt.target.value);
    }


    const onPasswordChange = (evt) => {
        setPassword(old => evt.target.value);
    }


    const isPureEnter = (keyEvent) => {
        try {
            if (keyEvent.key === "Enter" &&
                !keyEvent.altKey &&
                !keyEvent.ctrlKey &&
                !keyEvent.shiftKey)
                return true;
        }
        catch {
        }
        return false;
    };


    const onLoginClick = useCallback(() => {
        if (!toasterRef.current)
        {
            toasterRef.current = Toaster.create({
                canEscapeKeyClear:true,
                position:Position.TOP,
                usePortal:true
            });
        }

        setIsWorking(true);

        login(email, password, false)
            .then(() => {
                setPassword('');
            })
            .catch(response => {
                console.log(response)
                if (isString(response)) {
                    toasterRef.current.show({
                        message:`* ${'Error'}: ${response}`,
                        intent:Intent.DANGER,
                        timeout:3000
                    });
                }
                else if (isObject(response) && (response.status || 200) !== 200) {
                    toasterRef.current.show({
                        message:`* ${'Error'}: ${response.data.msg}`,
                        intent:Intent.DANGER,
                        timeout:3000
                    });
                }
                // else if (isNumber(response)) {
                //     setLoginResult({
                //         show:true,
                //         message:`* ${t('Error')}: ${t('You have no right to use builder tool')}`
                //     });
                // }
                else {
                    toasterRef.current.show({
                        message:`* Error: ${response}`,
                        intent:Intent.DANGER,
                        timeout:3000
                    });
                }

                setIsWorking(false);
            })
        ;
    }, [email, password, setIsWorking, setPassword]);


    const showHidePasswordButton = (
        <Tooltip2 content={`${showPassword ? 'Jelszó elrejtése' : 'Jelszó mutatása'}`}>
            <Button
                icon={showPassword ? 'eye-open' : 'eye-off'}
                intent={Intent.WARNING}
                minimal
                small
                disabled={isWorking}
                onClick={() => setShowPassword(prevState => !prevState)}
            />
        </Tooltip2>
    );



    useEffect(() => {
        if (user) {
            navigate(PagePath.ROOT);
        }
    }, [user]);


    return (
        <Wrapper>
            <FormCard interactive={false} elevation={Elevation.THREE}>
                <Logo src={logoSvg}/>
                <Form>
                    <FormGroup style={{width:'100%'}}>
                        <InputGroup
                            id={generateId('email')}
                            large
                            placeholder={'e-mail'}
                            autoFocus
                            value={email}
                            disabled={isWorking}
                            readOnly
                            onFocus={(evt) => evt.target.removeAttribute('readonly')}
                            onChange={onEmailChange}
                            onKeyDown={(e) => { try { if (isPureEnter(e)) passwordInput?.focus();} catch(ex) {} } }
                        />
                    </FormGroup>
                    <FormGroup style={{width:'100%', paddingBottom: '1rem'}}>
                        <InputGroup
                            inputRef={(inputRef) => passwordInput = inputRef}
                            id={generateId('pssword')}
                            large
                            placeholder='jelszó'
                            rightElement={showHidePasswordButton}
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            disabled={isWorking}
                            readOnly
                            onFocus={(evt) => evt.target.removeAttribute('readonly')}
                            onChange={onPasswordChange}
                            onKeyDown={(e) => { try { if (isPureEnter(e)) onLoginClick();} catch(ex) {} } }
                        />
                    </FormGroup>
                    <Button
                        id={generateId('loginbtn')}
                        text={'Bejelentkezés'}
                        intent={Intent.PRIMARY}
                        large
                        loading={isWorking}
                        onClick={onLoginClick}
                    />
                </Form>
                <div style={{
                    width:'100%',
                    marginTop:'1rem',
                    display: 'flex',
                    justifyContent: 'end'
                }}>
                    <DarkModeSwitch />
                </div>
            </FormCard>
        </Wrapper>
    );
}